import { APP_BASE_URL } from "@/constants/app-defaults";

/**
 * Centralized SEO constants, defaults, and templates.
 * Keeping raw text blocks, defaults, and complex string formatting structures unified.
 */

// 1. Base default fallbacks
export const DEFAULT_SEO = {
  TITLE: "Tooffi | Gifts from India's Most Passionate Creators",
  DESCRIPTION:
    "Discover unique, handcrafted gifts from India's finest artisans. Curated gift hampers, flowers, decor and more. Every gift should mean something.",
  IMAGE: `${APP_BASE_URL}/logo.png`,
  URL: APP_BASE_URL,
};

// 2. Homepage SEO defaults
export const HOMEPAGE_SEO = {
  TITLE: "Tooffi | Gifts from India's Most Passionate Creators",
  DESCRIPTION:
    "Discover unique, handcrafted gifts from India's finest artisans. Curated gift hampers, flowers, decor and more. Every gift should mean something.",
};

function getPossessiveName(name: string): string {
  if (
    name.endsWith("'s") ||
    name.endsWith("’s") ||
    name.endsWith("'") ||
    name.endsWith("’")
  ) {
    return name;
  }
  if (name.endsWith("s") || name.endsWith("S")) {
    return `${name}'`;
  }
  return `${name}'s`;
}

// 4. Dynamic SEO templates
export const SEO_TEMPLATES = {
  /**
   * Product Page Title template (~488 pages)
   * Formula: [Product Name] | [Vendor Business Name] | Tooffi
   */
  productTitle: (productName: string, businessName: string): string => {
    const formattedName = productName || "Custom Cake";
    if (businessName) {
      return `${formattedName} | ${businessName} | Tooffi`;
    }
    return `${formattedName}`;
  },

  /**
   * Product Page Description template
   * Formula: Order [Product Name] online by [Vendor Business Name] in Bangalore. [Fallback default]
   */
  productDescription: (
    productName: string,
    businessName: string,
    baseDesc?: string,
  ): string => {
    const name = productName || "custom cake";
    if (baseDesc) return baseDesc;
    if (businessName) {
      return `Order ${name} online, freshly baked by ${businessName} in Bangalore. Premium quality ingredients, reliable delivery, and beautiful designs on Tooffi.`;
    }
    return `Order ${name} online, freshly baked by top home bakers in Bangalore. Custom designs, premium ingredients, and secure delivery via Tooffi.`;
  },

  /**
   * Seller/Vendor/Maker Page Title template
   * Formula: <Maker name>: Handmade Gifts from <City> | Tooffi
   */
  sellerTitle: (
    businessName: string,
    _categories?: string | string[],
    city?: string,
  ): string => {
    const name = businessName || "Maker";
    const cityName = city?.trim() || "India";
    return `${name}: Handmade Gifts from ${cityName} | Tooffi`;
  },

  /**
   * Seller/Vendor/Maker Page Description template
   * Formula: Shop <Maker name>'s handmade gifts from <City> on Tooffi. Thoughtful, small-batch pieces for Diwali, birthdays and every celebration, delivered to your door.
   */
  sellerDescription: (
    businessName: string,
    _categories?: string | string[],
    city?: string,
    baseDesc?: string,
  ): string => {
    if (baseDesc) return baseDesc;
    const name = businessName || "our maker";
    const cityName = city?.trim() || "India";
    const possessive = getPossessiveName(name);
    return `Shop ${possessive} handmade gifts from ${cityName} on Tooffi. Thoughtful, small-batch pieces for Diwali, birthdays and every celebration, delivered to your door.`;
  },

  /**
   * Product Image Alt tag template
   * Formula: [Product Name] by [Vendor Name] | Tooffi
   */
  productImageAlt: (productName: string, vendorName?: string): string => {
    const name = productName || "Premium Cake";
    if (vendorName) {
      return `${name} by ${vendorName} | Tooffi`;
    }
    return `${name} | Tooffi`;
  },
};

/**
 * Manual metadata overrides map (by vendor id or slug).
 * Allows custom titles and descriptions (e.g. for Diwali makers) to be supplied
 * while preserving fallbacks and database metadata.
 */
export const MAKER_SEO_OVERRIDES: Record<
  string | number,
  { title?: string; description?: string }
> = {};
